import { Receipt } from 'lucide-react'
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { useSession } from '@/lib/auth-client'
import { CategoriesPage } from '@/routes/app/categories'
import { ItemDetailPage } from '@/routes/app/item-detail'
import { EditItemPage } from '@/routes/app/item-edit'
import { NewItemPage } from '@/routes/app/item-new'
import { ItemsListPage } from '@/routes/app/items-list'
import { AppLayout } from '@/routes/app/layout'
import {
	BillingPage,
	BillingSuccessPage,
} from '@/routes/app/settings/billing'
import { SettingsLayout } from '@/routes/app/settings/layout'
import { MembersPage } from '@/routes/app/settings/members'
import { IndexPage } from '@/routes/index'
import { InvitePage } from '@/routes/invite'
import { NotFoundPage } from '@/routes/not-found'
import { SignInPage } from '@/routes/sign-in'
import { SignUpPage } from '@/routes/sign-up'

function MarketingLayout({ children }: { children: React.ReactNode }) {
	const session = useSession()
	return (
		<div className="flex min-h-dvh flex-col">
			<header className="border-b">
				<div className="container mx-auto flex h-14 max-w-5xl items-center justify-between px-4">
					<Link
						to="/"
						className="flex items-center gap-2 font-semibold tracking-tight"
					>
						<div className="flex size-7 items-center justify-center rounded-md bg-primary text-primary-foreground">
							<Receipt className="size-4" />
						</div>
						Warrantly
					</Link>
					<nav className="flex items-center gap-2 text-sm">
						{session.data ? (
							<Button asChild size="sm">
								<Link to="/app">Open app</Link>
							</Button>
						) : (
							<>
								<Button asChild variant="ghost" size="sm">
									<Link to="/login">Sign in</Link>
								</Button>
								<Button asChild size="sm">
									<Link to="/register">Get started</Link>
								</Button>
							</>
						)}
					</nav>
				</div>
			</header>
			<main className="flex-1">{children}</main>
		</div>
	)
}

export default function App() {
	const location = useLocation()
	const isAppRoute = location.pathname.startsWith('/app')
	const isInviteRoute = location.pathname.startsWith('/invite')

	if (isInviteRoute) {
		return (
			<Routes>
				<Route path="invite/:token" element={<InvitePage />} />
			</Routes>
		)
	}

	if (isAppRoute) {
		return (
			<Routes>
				<Route path="app" element={<AppLayout />}>
					<Route index element={<ItemsListPage />} />
					<Route path="items/new" element={<NewItemPage />} />
					<Route path="items/:id" element={<ItemDetailPage />} />
					<Route path="items/:id/edit" element={<EditItemPage />} />
					<Route path="categories" element={<CategoriesPage />} />
					<Route path="settings" element={<SettingsLayout />}>
						<Route
							index
							element={<Navigate to="/app/settings/members" replace />}
						/>
						<Route path="members" element={<MembersPage />} />
						<Route path="billing" element={<BillingPage />} />
						<Route path="billing/success" element={<BillingSuccessPage />} />
					</Route>
				</Route>
				<Route
					path="*"
					element={
						<MarketingLayout>
							<NotFoundPage />
						</MarketingLayout>
					}
				/>
			</Routes>
		)
	}

	return (
		<MarketingLayout>
			<Routes>
				<Route index element={<IndexPage />} />
				<Route path="login" element={<SignInPage />} />
				<Route path="register" element={<SignUpPage />} />
				<Route path="*" element={<NotFoundPage />} />
			</Routes>
		</MarketingLayout>
	)
}
